import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { BellRing, Eye, X } from 'lucide-react';
import api from '../../../utils/authUtils';
import ReminderSendModal, { formatDay } from './ReminderSendModal';
import MarkDeclinedModal from './MarkDeclinedModal';
import { NextReminder } from './InviteReminders';
import { Card, CardTitle, Button, EngList, EngRow, WORKSPACE } from './ui';
import { eventNameFor } from '../../../utils/eventName';

// The speaker detail page's view of invite reminders: this speaker's
// invitations that are still waiting for an answer, where the reminders
// stand, and a manual "send one now". Renders nothing when nothing is waiting.
const SpeakerReminders = ({ profileId, refreshKey, onChanged }) => {
  const intl = useIntl();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(null);
  const [declining, setDeclining] = useState(null);
  const load = useCallback(() => {
    api.get('/api/cms/invite-reminders/pending').then(({ data: response }) => setData(response)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const mine = (data?.invites || []).filter(i => i.speakerProfileId === profileId);
  if (mine.length === 0) return null;
  const max = data.maxAutomaticReminders;

  return (
    <Card style={{ borderLeft: '4px solid #5b21b6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '.75rem' }}>
        <CardTitle style={{ margin: 0 }}><BellRing size={17} style={{ verticalAlign: '-3px', marginRight: '.35rem' }} />
          <FormattedMessage id="inviteReminders.detail.title" defaultMessage="Invitation not answered yet" />
        </CardTitle>
        <Link to={`${WORKSPACE}/invite-reminders?tab=template`} style={{ fontSize: '.8rem', color: '#0369a1' }}>
          <FormattedMessage id="inviteReminders.detail.editTemplate" defaultMessage="Edit the reminder email" />
        </Link>
      </div>
      <EngList>
        {mine.map(invite => (
          <EngRow key={invite.invitationId} style={{ padding: '.7rem .8rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '14rem', fontSize: '.85rem', lineHeight: 1.6 }}>
              <strong style={{ color: '#111D4E' }}>{eventNameFor(intl.locale, invite.eventName, invite.eventNameEN)}</strong>
              <div style={{ color: '#475569' }}>
                <FormattedMessage id="inviteReminders.send.history"
                  defaultMessage="Invited {invited} · last email {last} · {count, plural, =0 {no reminders yet} one {# reminder so far} other {# reminders so far}}"
                  values={{ invited: formatDay(intl, invite.invitedAt), last: formatDay(intl, invite.lastSentAt), count: invite.reminderCount }} />
              </div>
              <div style={{ color: '#475569' }}>
                <FormattedMessage id="inviteReminders.col.next" defaultMessage="Next automatic reminder" />: <NextReminder invite={invite} maxAutomatic={max} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <Button onClick={() => setSending([invite])}><Eye size={15} />
                <FormattedMessage id="inviteReminders.detail.send" defaultMessage="Preview & send reminder" />
              </Button>
              <Button secondary onClick={() => setDeclining(invite)}><X size={15} />
                <FormattedMessage id="inviteReminders.decline.submit" defaultMessage="Mark as declined" />
              </Button>
            </div>
          </EngRow>
        ))}
      </EngList>
      {sending && <ReminderSendModal invites={sending} maxAutomatic={max} onClose={() => setSending(null)} onSent={load} />}
      {declining && <MarkDeclinedModal invite={declining} onClose={() => setDeclining(null)} onDone={() => { load(); onChanged?.(); }} />}
    </Card>
  );
};

export default SpeakerReminders;
